import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getAdminOverview } from "@/lib/admin-overview.functions";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/admin/overview")({
  component: AdminOverviewPage,
});

function AdminOverviewPage() {
  const fetch = useServerFn(getAdminOverview);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-overview"],
    queryFn: () => fetch({}),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading platform stats…</p>;
  if (error) return <p className="text-sm text-destructive">{(error as Error).message}</p>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Accounts" value={data.accounts.total} sub={`+${data.accounts.new_30d} new (30d)`} />
        <Stat label="Workspaces" value={data.workspaces_total} />
        <Stat label="Contacts" value={data.contacts_total.toLocaleString()} />
        <Stat label="Transactions" value={data.transactions_total.toLocaleString()} />
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card title="AI usage, today">
          <Row k="Calls" v={data.ai_today.calls.toLocaleString()} />
          <Row k="Cache hits" v={data.ai_today.cache_hits.toLocaleString()} />
          <Row k="Cost" v={`$${data.ai_today.cost_usd.toFixed(2)}`} />
          <Row
            k="Tokens (in/out)"
            v={`${data.ai_today.input_tokens.toLocaleString()} / ${data.ai_today.output_tokens.toLocaleString()}`}
          />
        </Card>
        <Card title="AI usage, last 30 days">
          <Row k="Calls" v={data.ai_30d.calls.toLocaleString()} />
          <Row k="Cache hits" v={data.ai_30d.cache_hits.toLocaleString()} />
          <Row k="Cost" v={`$${data.ai_30d.cost_usd.toFixed(2)}`} />
          <Row
            k="Cache hit rate"
            v={
              data.ai_30d.calls + data.ai_30d.cache_hits > 0
                ? `${Math.round((data.ai_30d.cache_hits / (data.ai_30d.calls + data.ai_30d.cache_hits)) * 100)}%`
                : ","
            }
          />
        </Card>
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card title="Accounts by plan">
          {Object.entries(data.accounts.by_plan).map(([k, v]) => (
            <Row key={k} k={k} v={String(v)} />
          ))}
          {data.accounts.comped > 0 && (
            <Row k="Admin-comped" v={String(data.accounts.comped)} />
          )}
        </Card>
        <Card title="Accounts by status">
          {Object.entries(data.accounts.by_status).map(([k, v]) => (
            <Row key={k} k={k} v={String(v)} />
          ))}
        </Card>
      </section>

      <Card title="Top accounts, AI spend (30d)">
        {data.top_accounts_30d.length === 0 && (
          <p className="text-sm text-muted-foreground">No AI usage in the last 30 days.</p>
        )}
        <div className="divide-y divide-border">
          {data.top_accounts_30d.map((a) => (
            <div key={a.account_id} className="flex items-center justify-between py-2 text-sm">
              <span className="font-medium">{a.name}</span>
              <span className="text-muted-foreground">
                {a.calls_30d.toLocaleString()} calls · ${a.cost_30d_usd.toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      </Card>

      <section className="flex flex-wrap gap-2">
        <Link to="/admin/proposals">
          <Badge variant="secondary">{data.pending_proposals} pending proposals</Badge>
        </Link>
        <Link to="/admin/patterns">
          <Badge variant="secondary">{data.cross_account_patterns} cross-account patterns</Badge>
        </Link>
      </section>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <h3 className="mb-2 text-sm font-medium">{title}</h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium tabular-nums">{v}</span>
    </div>
  );
}
